import { latestLuteal, lutealOf, phaseAverage, previousWindowAverage, windowItems } from '../context';
import { fr } from '../dates';
import { bin, deltaText, mean, pl, r1 } from '../format';
import { ORNG, PHASES, RAMP_FG, RUST } from '../palette';
import { NA_CELL, bars, cycleColumn, cycleLabel, cycleLegend, detailSection, heatCell } from './shared';

// PMS and Cravings are scored on the luteal phase: the server's
// `by_phase.luteal` over the server's luteal span (`lutealOf`). The backend has
// no pre-period window (README gap G2), and FlexBoard computes none. The phase
// is an estimate of ~13 days, so every label says "est.".

export const NO_LUTEAL_NOTE = {
  pms: 'No luteal-phase symptom data yet',
  cravings: 'No luteal-phase craving data yet',
};

/** "cycle 4 luteal phase", or "previous luteal phase" for the first cycle fetched. */
const prevLabel = (cycle) => (cycle.n > 1 ? `cycle ${cycle.n - 1} luteal phase` : 'previous luteal phase');

const placeholderKpis = (labels, note) =>
  labels.map((label, i) => ({ label, value: '—', unit: '', scope: i === 0 ? note : '', note: '' }));

/** "↑ 1.8" luteal over follicular for the cycle, as a secondary KPI. */
function follicularKpi(ctx, metric, w, cycle) {
  const fol = phaseAverage(ctx, metric, cycle, 'follicular');
  return {
    label: 'Luteal vs follicular',
    value: w.average != null && fol != null ? deltaText(w.average, fol) : '—',
    unit: '',
    scope: fol == null ? 'No follicular days logged' : `Follicular ${r1(fol)}/5 in cycle ${cycle.n}`,
    note: 'A rise before the period points to a cyclical pattern',
  };
}

/** Trend bars of each cycle's luteal average; the current cycle shows its luteal days so far, once begun. */
function lutealTrend(x, of, cyc, color, title, scope, note) {
  const avgOf = (c) => of(c)?.average ?? null;
  const avg = mean(x.comp.map(avgOf));
  return {
    ...bars(
      cyc.map((c) => {
        const v = avgOf(c);
        return { ...cycleLabel(c), v, txt: v == null ? '' : r1(v), ongoing: !c.completed, empty: c.completed ? 'No data' : 'Luteal phase not reached' };
      }),
      0, 5, color, avg, `Avg ${r1(avg)}`, [0, 1, 2, 3, 4, 5]
    ),
    title,
    scope,
    legend: cycleLegend(color, x.rl, { ongoing: cyc.some((c) => !c.completed && avgOf(c) != null) }),
    hasNote: Boolean(note),
    note,
  };
}

/** Rows of item names, sorted by the latest window's value. */
function itemOrder(of, comp, key) {
  const names = new Map();
  comp.forEach((c) => windowItems(of(c)).forEach((it) => {
    if (!names.has(it.name)) names.set(it.name, -1);
  }));
  const last = windowItems(of(comp[comp.length - 1]));
  last.forEach((it) => names.set(it.name, it[key] ?? -1));
  return [...names.entries()].sort((a, b) => b[1] - a[1]).map(([n]) => n);
}

const itemOf = (w, name) => {
  if (!w) return undefined;
  return windowItems(w).find((i) => i.name === name) || null;
};

export function pmsScreen(ctx, x) {
  const cyc = ctx.cur ? x.comp.concat([ctx.cur]) : x.comp;
  const of = (c) => lutealOf(ctx, 'pms', c);
  const found = latestLuteal(ctx, 'pms');
  let kpis;
  let lists = [];
  if (found) {
    const { w, cycle } = found;
    const items = windowItems(w);
    const bySev = items.slice().sort((a, b) => (b.average ?? 0) - (a.average ?? 0)).slice(0, 3);
    const byFreq = items.slice().sort((a, b) => b.days - a.days || (b.average ?? 0) - (a.average ?? 0)).slice(0, 3);
    const missing = w.days_total - w.days_logged;
    kpis = [
      { label: 'Luteal symptom score', value: r1(w.average), unit: '/5', scope: `Cycle ${cycle.n} · ${fr(w.start_date, w.end_date)} · luteal phase (est.)`, note: `${deltaText(w.average, previousWindowAverage(found))} vs ${prevLabel(cycle)}` },
      follicularKpi(ctx, 'pms', w, cycle),
      { label: 'Symptom days', value: `${w.active_days} of ${w.days_logged}`, unit: '', scope: 'Logged luteal days rated above 0', note: '' },
      { label: 'Days logged', value: `${w.days_logged} of ${w.days_total}`, unit: '', scope: 'In the luteal phase', note: missing > 0 ? `${pl(missing, 'day')} with no entry` : 'Every day logged' },
    ];
    lists = [
      { title: 'Highest average severity', scope: `Luteal phase ${fr(w.start_date, w.end_date)}`, items: bySev.map((p) => ({ name: p.name, txt: `${r1(p.average)}/5` })) },
      { title: 'Most frequent', scope: `Days rated above 0, of ${w.days_logged} logged`, items: byFreq.map((p) => ({ name: p.name, txt: `${p.days} of ${w.days_logged} days` })) },
    ].filter((l) => l.items.length);
  } else {
    kpis = placeholderKpis(['Luteal symptom score', 'Luteal vs follicular', 'Symptom days', 'Days logged'], NO_LUTEAL_NOTE.pms);
  }

  const trend = lutealTrend(x, of, cyc, '#B5654A', 'PMS score by cycle',
    `Luteal phase symptom score (est., the ~13 days before each period) · 0–5 · ${x.rl}`,
    found ? 'Luteal score = all symptom ratings in the luteal phase ÷ number of ratings. Phase dates are estimated from the period log.' : `${NO_LUTEAL_NOTE.pms}.`);

  const order = itemOrder(of, x.comp, 'average');
  const details = [
    detailSection('heat', 'All symptoms by cycle', 'Average severity of each symptom within each luteal phase (est.)', {
      isBlock: true,
      isTrend: false,
      block: {
        type: 'heat',
        corner: 'SYMPTOM',
        cols: x.comp.map((c) => `C${c.n}`),
        rows: order.map((name) => ({ name, weight: 500, cells: x.comp.map((c) => heatCell(itemOf(of(c), name)?.average ?? null, 5, RUST, r1)) })),
        foot: order.length
          ? '“—” = symptom not rated in that luteal phase. The current cycle is omitted: its luteal phase is not complete.'
          : `${NO_LUTEAL_NOTE.pms}.`,
      },
    }, true),
  ];
  return { kpis, lists, trend, details };
}

/** Craving-type cell: days rated above 0, out of the logged days in that luteal phase. */
function daysCell(w, name) {
  const it = itemOf(w, name);
  if (it === undefined) return heatCell(null);
  const v = it?.days ?? 0;
  const logged = w.days_logged;
  if (v === 0) return { txt: '0', bg: '#fff', fg: '#1f1f1f', ring: 'inset 0 0 0 1px #CFC4BB', tip: `0 of ${pl(logged, 'logged day')}` };
  const i = bin(v, Math.max(logged, 1));
  return { txt: `${v}d`, bg: ORNG[i], fg: RAMP_FG[i], ring: 'none', tip: `${v} of ${pl(logged, 'logged day')}` };
}

export function cravingsScreen(ctx, x) {
  const cyc = ctx.cur ? x.comp.concat([ctx.cur]) : x.comp;
  const of = (c) => lutealOf(ctx, 'cravings', c);
  const found = latestLuteal(ctx, 'cravings');
  let kpis;
  let lists = [];
  if (found) {
    const { w, cycle } = found;
    const items = windowItems(w);
    const byF = items.slice().sort((a, b) => b.days - a.days || (b.average ?? 0) - (a.average ?? 0));
    const byA = items.slice().sort((a, b) => (b.average ?? 0) - (a.average ?? 0));
    // A logged day with nothing above 0 is a "No cravings" day (real cravings are rated 1–5).
    const zero = w.days_logged - w.active_days;
    kpis = [
      { label: 'Average craving intensity', value: r1(w.average), unit: '/5', scope: `Cycle ${cycle.n} · ${fr(w.start_date, w.end_date)} · luteal phase (est.)`, note: `${deltaText(w.average, previousWindowAverage(found))} vs ${prevLabel(cycle)}` },
      byF.length
        ? { label: 'Most common type', value: byF[0].name, unit: '', scope: `${byF[0].days} of ${w.days_logged} logged days rated above 0`, note: 'Frequency = distinct days' }
        : { label: 'Most common type', value: '—', unit: '', scope: 'No cravings rated', note: '' },
      follicularKpi(ctx, 'cravings', w, cycle),
      { label: 'Days logged', value: `${w.days_logged} of ${w.days_total}`, unit: '', scope: 'In the luteal phase', note: `Incl. ${zero} “No cravings today” ${zero === 1 ? 'entry' : 'entries'}` },
    ];
    lists = [
      { title: 'Frequency', scope: 'Days rated above 0', items: byF.slice(0, 3).map((p) => ({ name: p.name, txt: `${p.days} of ${w.days_logged} days` })) },
      { title: 'Average intensity', scope: 'On days rated', items: byA.slice(0, 3).map((p) => ({ name: p.name, txt: `${r1(p.average)}/5` })) },
    ].filter((l) => l.items.length);
  } else {
    kpis = placeholderKpis(['Average craving intensity', 'Most common type', 'Luteal vs follicular', 'Days logged'], NO_LUTEAL_NOTE.cravings);
  }

  const trend = lutealTrend(x, of, cyc, '#F26A1F', 'Luteal craving intensity by cycle',
    `Average intensity in the luteal phase (est., the ~13 days before each period) · 0–5 · ${x.rl}`,
    found ? '' : `${NO_LUTEAL_NOTE.cravings}.`);

  const order = itemOrder(of, x.comp, 'days');
  const phaseCell = (c, p) => {
    const v = ctx.analytics.cravings[c.start]?.by_phase?.[p.toLowerCase()]?.average ?? null;
    return v == null && !c.completed && p === 'Luteal' ? { ...NA_CELL, tip: 'Phase unavailable' } : heatCell(v, 5, ORNG, r1);
  };
  const details = [
    detailSection('types', 'Craving types by cycle', 'Days each category was rated above 0, per luteal phase (est.)', {
      isBlock: true,
      isTrend: false,
      block: {
        type: 'heat',
        corner: 'CATEGORY',
        cols: x.comp.map((c) => `C${c.n}`),
        rows: order.map((name) => ({ name, weight: 500, cells: x.comp.map((c) => daysCell(of(c), name)) })),
        foot: order.length ? 'Value = days the category was rated above 0 in that luteal phase; shade = share of that phase’s logged days.' : `${NO_LUTEAL_NOTE.cravings}.`,
      },
    }, true),
    detailSection('phase', 'Cravings by phase, per cycle', 'Average intensity per estimated phase', {
      isBlock: true,
      isTrend: false,
      block: {
        type: 'heat',
        corner: 'PHASE',
        cols: cyc.map(cycleColumn),
        rows: PHASES.map((p) => ({ name: p, weight: 500, cells: cyc.map((c) => phaseCell(c, p)) })),
        foot: 'Describes reported patterns only; does not establish a hormonal cause.',
      },
    }, false),
  ];
  return { kpis, lists, trend, details };
}
